import { sourceReviewGaps } from './sourceGapPolicy.js';

export type EpIntakeField = {
    key: string;
    label: string;
    kind: 'text' | 'date' | 'number' | 'select' | 'textarea';
    options?: string[];
    conditional?: boolean;
};

// The origin is the additional client input agreed for route generation.
export const EP_V2_CLIENT_FIELDS: EpIntakeField[] = [
    { key: 'principalName', label: 'Principal full name', kind: 'text' },
    { key: 'principalTitleOrganization', label: 'Principal title and organization', kind: 'text' },
    { key: 'eventName', label: 'Event name', kind: 'text' },
    { key: 'eventDate', label: 'Event date', kind: 'date' },
    { key: 'eventType', label: 'Event type', kind: 'select', options: ['Public speech', 'Private meeting', 'Site visit', 'Conference', 'Other'] },
    { key: 'venueName', label: 'Venue name', kind: 'text' },
    { key: 'venueCountry', label: 'Venue country', kind: 'text' },
    { key: 'venueState', label: 'Venue state or province', kind: 'text' },
    { key: 'venueAddress', label: 'Venue street address, city, and postal code', kind: 'textarea' },
    { key: 'criticalJourneys', label: 'Critical journeys, one origin | destination per line', kind: 'textarea' },
    { key: 'threatClass', label: 'Threat level assessment', kind: 'select', options: ['Low', 'Medium', 'High'] },
    { key: 'advanceAgentCount', label: 'Number of advance agents', kind: 'number' },
    { key: 'safeRoomRequired', label: 'Safe room required', kind: 'select', options: ['Yes', 'No'] },
    { key: 'knownThreatActors', label: 'Known threat actors', kind: 'textarea' },
    { key: 'epProjectReference', label: 'Linked EP project reference', kind: 'text', conditional: true },
    { key: 'radioSystem', label: 'Advance team communications system', kind: 'text' },
];

export const EP_V2_CLIENT_GROUPS = [
    ['Principal full name', 'principalName'],
    ['Principal title and organization', 'principalTitleOrganization'],
    ['Event name and date', 'eventName', 'eventDate'],
    ['Event type', 'eventType'],
    ['Venue name', 'venueName'],
    ['Venue address', 'venueCountry', 'venueState', 'venueAddress'],
    ['Critical journeys', 'criticalJourneys'],
    ['Threat level assessment', 'threatClass'],
    ['Number of advance agents', 'advanceAgentCount'],
    ['Safe room required', 'safeRoomRequired'],
    ['Known threat actors', 'knownThreatActors'],
    ['Linked EP project', 'epProjectReference'],
    ['Advance team communications', 'radioSystem'],
] as const;

export const EP_V2_REVIEW_ITEMS = [
    'Venue address and mapped location reviewed',
    'Route candidates and missing PACE roles reviewed',
    'Threat rating and source dates reviewed',
    'Hospital and emergency service candidates reviewed',
    'Safe room criteria and unknowns reviewed',
    'Liaison and communications details reviewed',
    'All five documents reviewed and corrected',
] as const;

export function missingEpV2Intake(values: Record<string, unknown>) {
    const missing = EP_V2_CLIENT_FIELDS.filter(field => !field.conditional && !String(values[field.key] ?? '').trim())
        .map(field => field.label);
    if (values.advanceAgentCount && (!Number.isInteger(Number(values.advanceAgentCount)) || Number(values.advanceAgentCount) < 1))
        missing.push('Number of advance agents must be positive');
    if (values.eventDate && !/^\d{4}-\d{2}-\d{2}$/.test(String(values.eventDate)))
        missing.push('Event date must be valid');
    if (values.criticalJourneys && !parseEpJourneys(String(values.criticalJourneys)).length)
        missing.push('Critical journeys need origin | destination pairs');
    if (values.criticalJourneys && String(values.criticalJourneys).split(/\r?\n/).filter(Boolean).length !== parseEpJourneys(String(values.criticalJourneys)).length)
        missing.push('Every critical journey needs one origin | destination pair');
    for (const key of ['eventType', 'threatClass', 'safeRoomRequired']) {
        const field = EP_V2_CLIENT_FIELDS.find(item => item.key === key)!;
        if (values[key] && !field.options?.includes(String(values[key])))
            missing.push(`${field.label} must use a listed option`);
    }
    return missing;
}

export type EpJourney = { origin: string; destination: string };
export type EpRouteCandidate = { label: string; distanceMeters: number; durationMinutes: number; staticMinutes: number; description: string; steps: string[]; pathKey?: string };
export type EpJourneyResearch = EpJourney & { routes: EpRouteCandidate[] };

export function normalizeEpRoutes(value: unknown): EpRouteCandidate[] {
    const routes: EpRouteCandidate[] = [];
    const seen = new Set<string>();
    for (const item of Array.isArray(value) ? value.slice(0, 32) : []) {
        if (!item || typeof item !== 'object')
            continue;
        const distanceMeters = Number(item.distanceMeters);
        const durationMinutes = Number(item.durationMinutes);
        const staticMinutes = Number(item.staticMinutes);
        const description = String(item.description || '').trim().slice(0, 300);
        const steps = (Array.isArray(item.steps) ? item.steps : []).map((step: unknown) => String(step || '').trim()).filter(Boolean);
        const pathKey = /^g:[a-f0-9]{16}$/.test(String(item.pathKey || '')) ? String(item.pathKey) : '';
        const identity = pathKey || `${description}|${steps.join('|')}`.toLowerCase();
        if (!Number.isFinite(distanceMeters) || distanceMeters <= 0 || !Number.isFinite(durationMinutes) || durationMinutes <= 0 ||
            (!description && !steps.length) || seen.has(identity))
            continue;
        seen.add(identity);
        routes.push({ label: String(item.label || '').trim().slice(0, 60), distanceMeters, durationMinutes,
            staticMinutes: Number.isFinite(staticMinutes) && staticMinutes >= 0 ? staticMinutes : 0,
            description, steps: steps.slice(0, 30).map((step: string) => step.slice(0, 240)), ...(pathKey ? { pathKey } : {}) });
        if (routes.length === 4)
            break;
    }
    return routes;
}

export function parseEpJourneys(value: string, legacyOrigin = '', legacyVenue = ''): EpJourney[] {
    if (!value.trim() && legacyOrigin.trim() && legacyVenue.trim())
        return [{ origin: legacyOrigin.trim(), destination: legacyVenue.trim() }];
    const journeys: EpJourney[] = [];
    for (const line of value.split(/\r?\n/)) {
        if (!line.trim())
            continue;
        const parts = line.split('|').map(part => part.trim());
        if (parts.length !== 2 || !parts[0] || !parts[1])
            return [];
        journeys.push({ origin: parts[0], destination: parts[1] });
        if (journeys.length > 12)
            return [];
    }
    return journeys;
}

const SPECIALTY_FACILITY = /\b(dental|dentist|maternity|obstetric|gynecolog|paediatric|pediatric|children'?s|eye|ophthalm|psychiatr|mental health|veterinar|cosmetic|fertility|rehab|clinic|nursing home)\b/i;

export function isGeneralOrTraumaHospital(name: string, primaryType?: string): boolean {
    return (!primaryType || primaryType === 'hospital') && !/^(none|unknown|unavailable|no verified|pending|not found)\b/i.test(name.trim()) &&
        !SPECIALTY_FACILITY.test(name) &&
        /\b(general hospital|trauma cent(?:er|re))\b/i.test(name);
}

export function epPaceSummary(journeys: EpJourneyResearch[]): string {
    const roles = ['Primary', 'Alternate', 'Contingency', 'Emergency'];
    return journeys.map((journey, index) => {
        const lines = [`Journey ${index + 1}: ${journey.origin} → ${journey.destination}`];
        for (let role = 0; role < roles.length; role++) {
            const route = journey.routes[role];
            lines.push(route
                ? `${roles[role]}: Mapped candidate via ${route.description || route.steps[0] || 'unnamed roads'}; ${route.distanceMeters} m, ${route.durationMinutes} min estimate. Safety and use need consultant confirmation.`
                : `${roles[role]}: No distinct mapped route. Hold movement where safe, coordinate a verified option and local response, then record this open route gap.`);
        }
        return lines.join('\n');
    }).join('\n\n');
}

export function epManualPaceSummary(values: Record<string, unknown>): string {
    const pairs = parseEpJourneys(String(values.criticalJourneys || ''));
    const roles = [['Primary', 'primary'], ['Alternate', 'alternate'], ['Contingency', 'contingency'], ['Emergency', 'emergency']];
    for (const [, code] of roles) {
        const origin = String(values[`${code}Origin`] || '').trim();
        const destination = String(values[`${code}Destination`] || '').trim();
        if (origin && destination && !pairs.some(pair => pair.origin.toLowerCase() === origin.toLowerCase() &&
            pair.destination.toLowerCase() === destination.toLowerCase()))
            pairs.push({ origin, destination });
    }
    return pairs.map((pair, index) => {
        const lines = [`Journey ${index + 1}: ${pair.origin} → ${pair.destination}`];
        for (const [role, code] of roles) {
            const matches = String(values[`${code}Origin`] || '').trim().toLowerCase() === pair.origin.toLowerCase() &&
                String(values[`${code}Destination`] || '').trim().toLowerCase() === pair.destination.toLowerCase();
            const waypoints = String(values[`${code}Waypoints`] || '').trim();
            lines.push(matches && waypoints
                ? `${role}: Consultant supplied via ${waypoints}. Confirm road access, timing, and safety before use.`
                : `${role}: No distinct route documented for this journey. Hold movement where safe, coordinate a verified option and local response, then record this open route gap.`);
        }
        return lines.join('\n');
    }).join('\n\n');
}

export function epStandalonePaceSummary(values: Record<string, unknown>): string {
    const pairs = parseEpJourneys(String(values.critical_journeys || ''));
    const roles = [['Primary', 'primary_entry'], ['Alternate', 'secondary_entry'], ['Contingency', 'contingency_route'], ['Emergency', 'eer_route']];
    return pairs.map((pair, index) => {
        const lines = [`Journey ${index + 1}: ${pair.origin} → ${pair.destination}`];
        for (const [role, key] of roles) {
            const description = index === 0 ? String(values[key] || '').trim() : '';
            lines.push(description && !/^(no\b|none\b|unavailable\b|pending\b)/i.test(description)
                ? `${role}: Consultant supplied route: ${description}. Confirm access, timing, and safety before use.`
                : `${role}: No distinct route documented. Hold movement where safe, coordinate a verified option and local response, then record this open route gap.`);
        }
        return lines.join('\n');
    }).join('\n\n');
}

export function epV2ResearchGaps(research: Record<string, unknown>) {
    const gaps: string[] = [];
    const coordinates = research.coordinates as { latitude?: number; longitude?: number } | undefined;
    if (!Number.isFinite(coordinates?.latitude) || !Number.isFinite(coordinates?.longitude))
        gaps.push('Venue geocoding unavailable; confirm the venue location.');
    else if (research.geocodeExact !== true)
        gaps.push('Venue geocoding is approximate; confirm the venue location.');
    const journeys = Array.isArray(research.journeys) ? research.journeys as EpJourneyResearch[] : [];
    if (!journeys.length)
        gaps.push('Critical journeys are unavailable; confirm origin and destination pairs.');
    if (journeys.length)
        for (let index = 0; index < journeys.length; index++) {
            if (!Array.isArray(journeys[index].routes) || journeys[index].routes.length < 4)
                gaps.push(`Journey ${index + 1} has fewer than four distinct route candidates; verify remaining PACE roles.`);
        }
    else if (!(research.routes instanceof Array) || research.routes.length < 4)
        gaps.push('Fewer than four route candidates; verify remaining PACE roles.');
    if (!(research.hospitals instanceof Array) || !research.hospitals.length)
        gaps.push('No general hospital or trauma center candidate; confirm emergency care options.');
    if (!(research.police instanceof Array) || !research.police.length)
        gaps.push('Police candidates unavailable; identify the appropriate agency.');
    if (!(research.fireStations instanceof Array) || !research.fireStations.length)
        gaps.push('Fire or EMS candidates unavailable; identify local responders.');
    if (!research.fcdo && !research.dos)
        gaps.push('Official travel advisories unavailable; review current sources.');
    for (const error of Array.isArray(research.sourceErrors) ? research.sourceErrors : [])
        gaps.push(String(error));
    gaps.push('Local incident history needs current source review.');
    gaps.push('Route chokepoints and emergency use remain unconfirmed.');
    if (research.safeRoomRequired === 'Yes')
        gaps.push('Safe room location and capabilities are not established by online sources.');
    if (String(research.knownThreatActors || '').trim() && !/^none\b/i.test(String(research.knownThreatActors)))
        gaps.push('Named threat actors need current profile review.');
    gaps.push('AED access, trauma designation, and direct liaison contacts need confirmation.');
    return sourceReviewGaps(gaps);
}

export function epV2GroundGap(gap: string): boolean {
    return /^(Venue geocoding|Critical journeys are unavailable|Journey \d+ has fewer|Fewer than four route|No general hospital or trauma center|Route chokepoints|Safe room location|AED access)/i.test(gap);
}
